import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Button } from '../common';
import { Heading, Typography } from '../common/Typography';
import { Card } from '../common/Card';
import ClubLogo from '../clubs/ClubLogo';
import { homeSectionHeading, homeElevatedInset } from './homeDashboardStyles';

import { FrequentMatchupCard } from '../matchups/FrequentMatchupCard';
import {
  buildEventHeadline,
  getEventIcon,
  isOpponentNamedMatchupEvent,
} from '../../services/activityFeedService';
import { MatchupEventHeadline } from '../activity/MatchupEventHeadline';
import { interactiveBleedInsetX } from '../../constants/interactiveSafeInsets';

const homeMatchupsStripClass = `flex gap-4 overflow-x-auto pb-3 pt-1 ${interactiveBleedInsetX} scroll-smooth snap-x snap-mandatory sm:snap-none [scrollbar-gutter:stable_both-edges] touch-pan-x`;
const homeMatchupsCardClass = 'shrink-0 w-[min(100%,18.5rem)] sm:w-72 lg:w-80 snap-start';

/**
 * Card for a matchup activity event (first win, took lead, hot form, etc.)
 */
function MatchupEventCard({ event, onNavigate }) {
  const icon = getEventIcon(event.event_type);
  const useColoredHeadline = isOpponentNamedMatchupEvent(event.event_type);
  const headline = useColoredHeadline ? null : buildEventHeadline(event);
  const dateStr = event.occurred_at ? format(parseISO(event.occurred_at), 'MMM d, yyyy') : null;
  const canViewMatchHistory = Boolean(event.metadata?.opponent_id);

  return (
    <Card
      variant="default"
      className={`flex flex-col gap-3 ${homeElevatedInset} ${homeMatchupsCardClass}`}
    >
      <div className="flex items-center gap-2.5">
        <span
          className="inline-flex items-center justify-center w-9 h-9 rounded-xl text-lg shrink-0 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400"
          aria-hidden
        >
          {icon}
        </span>
        <Typography variant="caption" className="text-text-muted uppercase tracking-wide text-[0.65rem] font-semibold">
          Matchup
        </Typography>
      </div>

      <Typography className="font-semibold leading-snug text-text-primary text-sm line-clamp-3">
        {useColoredHeadline ? <MatchupEventHeadline event={event} /> : headline}
      </Typography>

      {(dateStr || event.club_name) && (
        <div className="flex items-end justify-between gap-2 mt-auto min-w-0">
          {dateStr ? (
            <Typography variant="caption" className="text-text-muted shrink-0">
              {dateStr}
            </Typography>
          ) : (
            <span />
          )}
          {event.club_name ? (
            <div className="flex items-center gap-1.5 min-w-0 max-w-[55%]" title={event.club_name}>
              {event.club_logo_image_url && (
                <ClubLogo
                  logoImageUrl={event.club_logo_image_url}
                  clubName={event.club_name}
                  size="sm"
                  className="shrink-0"
                />
              )}
              <Typography variant="caption" className="text-text-muted truncate text-right min-w-0">
                {event.club_name}
              </Typography>
            </div>
          ) : null}
        </div>
      )}

      {canViewMatchHistory ? (
        <div className="mt-auto">
          <Button
            variant="outline"
            size="small"
            className="w-full text-xs font-semibold"
            onClick={() => onNavigate(event)}
          >
            View match history
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

/**
 * HomeFrequentMatchups — shows matchup transition moments (first wins, taking the lead, etc.)
 * followed by the evergreen strip of familiar opponents from player_opponent_stats.
 */
export function HomeFrequentMatchups({ matchups, recentEvents, loading }) {
  const navigate = useNavigate();

  function handleViewHistory(matchup) {
    const params = new URLSearchParams({ opponentId: matchup.opponent_id });
    if (matchup.club_id) params.set('clubId', matchup.club_id);
    navigate(`/profile/matchups?${params.toString()}`);
  }

  function handleEventNavigate(event) {
    if (event.metadata?.opponent_id) {
      const params = new URLSearchParams({ opponentId: event.metadata.opponent_id });
      if (event.club_id) params.set('clubId', event.club_id);
      navigate(`/profile/matchups?${params.toString()}`);
    }
  }

  const hasEvents   = recentEvents && recentEvents.length > 0;
  const hasMatchups = matchups && matchups.length > 0;

  if (loading) {
    return (
      <div className="mb-10">
        <Heading level={2} className={`${homeSectionHeading} mb-5`}>Matchups</Heading>
        <div className="text-center py-8 rounded-2xl border border-border-medium bg-surface-subtle/60">
          <Typography variant="muted">Loading matchups...</Typography>
        </div>
      </div>
    );
  }

  if (!hasEvents && !hasMatchups) {
    return null;
  }

  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-5 gap-3">
        <Heading level={2} className={homeSectionHeading}>Matchups</Heading>
        <Button
          onClick={() => navigate('/profile/matchups')}
          variant="text"
          className="text-sm shrink-0 font-semibold text-primary"
        >
          View all
        </Button>
      </div>

      <div
        role="region"
        aria-label="Matchups"
        className={homeMatchupsStripClass}
      >
        {/* Matchup transition moments (first win, took lead, hot form, etc.) */}
        {hasEvents && recentEvents.slice(0, 3).map((event) => (
          <MatchupEventCard
            key={event.id}
            event={event}
            onNavigate={handleEventNavigate}
          />
        ))}

        {/* Evergreen frequent opponent cards */}
        {hasMatchups && matchups.slice(0, 3).map((matchup) => (
          <FrequentMatchupCard
            key={`${matchup.opponent_id}-${matchup.club_id}-${matchup.season_id}`}
            matchup={matchup}
            onViewHistory={handleViewHistory}
          />
        ))}
      </div>

      {(hasEvents || (hasMatchups && matchups.length > 1)) ? (
        <Typography variant="caption" className="mt-3 text-center text-text-muted px-2 sm:hidden">
          Swipe sideways for more · Use{' '}
          <span className="font-semibold text-text-secondary">View match history</span> on each card
        </Typography>
      ) : null}
    </div>
  );
}
